import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import { getFontCss } from '@oberoninternal/travelbase-ds/constants/theme';
import React, { FC } from 'react';
import styled from 'styled-components';
import Img from './Img';
import createImgProps, { ImageType } from '../utils/createImgProps';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { ActivityReview, Review } from '../generated/graphql';
import FormattedReviewTerm from './FormattedReviewTerm';
import ReviewStars from './ReviewStars';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import ReviewStar from './svg/ReviewStar';
import Chat from './svg/Chat';
import Accomodation from './svg/Accomodation';
import Button from '@oberoninternal/travelbase-ds/components/action/Button';

interface Props {
    title: string;
    thumbnail?: (ImageType & { type?: string }) | null;
    webThumbnail?: ImageType | null;
    arrivalDate: string;
    review?: Review | ActivityReview;
    onLeaveReviewClick?: () => void;
    onReviewClick?: () => void;
    isUnavailable?: boolean;
}

const ReviewTile: FC<React.PropsWithChildren<Props>> = ({
    title,
    thumbnail,
    webThumbnail,
    arrivalDate,
    review,
    onLeaveReviewClick,
    onReviewClick,
    isUnavailable,
}) => (
    <ReviewTileWrapper isUnavailable={isUnavailable} onClick={onReviewClick}>
        <TileContentWrapper>
            <TileInfoWrapper isReview={!review}>
                {!!thumbnail && !!webThumbnail && (
                    <ThumbnailWrapper>
                        <ThumbnailContainer>
                            <Img {...createImgProps(thumbnail, webThumbnail)} layout="fill" />
                        </ThumbnailContainer>
                    </ThumbnailWrapper>
                )}
                <TileInfoContentWrapper>
                    <EyeBrowWrapper>
                        <StyledAccomodation />
                        <EyeBrow>
                            {review?.__typename === 'Review'} ? (
                            <FormattedMessage defaultMessage="Accommodatie" />
                            ) : (
                            <FormattedMessage defaultMessage="Activiteit" />)
                        </EyeBrow>
                    </EyeBrowWrapper>
                    <ReviewTitleTitle>{title}</ReviewTitleTitle>
                    <StyledDate>
                        <FormattedMessage
                            defaultMessage="{date}"
                            values={{
                                date: <FormattedDate format="DAYMONTHYEAR" value={new Date(arrivalDate)} />,
                            }}
                        />
                    </StyledDate>
                </TileInfoContentWrapper>
            </TileInfoWrapper>
            {!review && (
                <ButtonWrapper>
                    <StyledButton onClick={onLeaveReviewClick}>
                        <FormattedMessage defaultMessage="Beoordelen" />
                    </StyledButton>
                </ButtonWrapper>
            )}
            <StyledDivider hasReview={!review} />
            {review && (
                <ReviewWrapper>
                    <HasReplyWrapper hasReply={!review.reply}>
                        {review.reply && (
                            <>
                                <StyledChat />
                                <ReviewBody className="gt-m">
                                    {review?.__typename === 'Review'} ? (
                                    <FormattedMessage defaultMessage="Reactie verhuurder" />
                                    ) : (
                                    <FormattedMessage defaultMessage="Reactie partner" />)
                                </ReviewBody>
                                <ReviewBody className="lt-m">
                                    <FormattedMessage defaultMessage="Reactie" />
                                </ReviewBody>
                            </>
                        )}
                    </HasReplyWrapper>
                    <RatingWrapper hasReply={!review.reply}>
                        <RatingItems>
                            <StyledReviewStar />
                            <ReviewBody>
                                <span>{review.average}</span>
                            </ReviewBody>
                        </RatingItems>
                        <RatingItems>
                            <ReviewBody>
                                <FormattedReviewTerm average={review.average} />
                            </ReviewBody>
                        </RatingItems>
                        <RatingItems mobileNone>
                            <ReviewStars average={review.average} />
                        </RatingItems>
                    </RatingWrapper>
                </ReviewWrapper>
            )}
        </TileContentWrapper>
    </ReviewTileWrapper>
);

export default ReviewTile;

const ReviewTileWrapper = styled.div<{ isUnavailable?: boolean }>`
    border: 1px solid rgba(0, 0, 0, 0.17);
    box-shadow: 0px 24px 14px rgba(0, 0, 0, 0.01), 0px 11px 11px rgba(0, 0, 0, 0.02), 0px 3px 6px rgba(0, 0, 0, 0.03),
        0px 0px 0px rgba(0, 0, 0, 0.03);
    border-radius: 0.8rem;
    margin-bottom: 3.2rem;

    ${({ isUnavailable }) => isUnavailable && 'opacity: 0.5;'}
`;

const TileContentWrapper = styled.div`
    display: flex;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.m}) {
        display: block;
        padding: 1.6rem;
    }
`;

const TileInfoWrapper = styled.div<{ isReview: boolean }>`
    display: flex;
    width: ${({ isReview }) => (isReview ? `80%` : `60%`)};
    align-items: center;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.m}) {
        width: 100%;
        margin-bottom: 1.2rem;
    }
`;

const ButtonWrapper = styled.div`
    display: flex;
    width: 20%;
    align-items: center;
    justify-content: flex-end;
    padding-right: 4.8rem;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.m}) {
        width: 100%;
        padding-right: 0;
    }
`;

const ReviewTitleTitle = styled(Title)`
    ${({ theme }) => getFontCss(theme.fontSizes.title.small)};
`;

const ThumbnailContainer = styled.div`
    position: relative;
    width: 16rem;
    height: 12rem;
    margin-right: ${({ theme }) => theme.spacing['50_Semi']};
    overflow: hidden;
    border-top-left-radius: 0.8rem;
    border-bottom-left-radius: 0.8rem;

    img {
        object-fit: cover;
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.m}) {
        border-top-left-radius: 0.8rem;
        border-bottom-left-radius: 0.8rem;
        width: 16rem;
        height: 12rem;
        flex-shrink: 0;
    }

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: 8rem;
        height: 8rem;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }

    @media screen and (max-width: 320px) {
        width: 6rem;
        height: 6rem;
    }
`;

const EyeBrowWrapper = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 0.4rem;
`;

const EyeBrow = styled(Body)`
    ${({ theme }) => getFontCss(theme.fontSizes.body.small)};
`;

const StyledAccomodation = styled(Accomodation)`
    margin-right: 0.4rem;
`;

const StyledDate = styled(Body)`
    ${({ theme }) => getFontCss(theme.fontSizes.body.small)};
`;

const StyledButton = styled(Button)`
    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.m}) {
        width: 100%;
    }
`;

const ThumbnailWrapper = styled.div`
    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: 30%;
    }
`;

const TileInfoContentWrapper = styled.div`
    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.m}) {
        width: 70%;
        padding-left: 1.2rem;
    }
`;

const ReviewWrapper = styled.div`
    width: 40%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-right: 4rem;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: 100%;
        padding-right: 0;
    }
`;

const RatingWrapper = styled.div<{ hasReply: boolean }>`
    padding-left: 2rem;
    display: flex;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        padding-left: 0;
        width: ${({ hasReply }) => (hasReply ? '100%' : '50%')};
        justify-content: center;
        order: 1;
    }
`;

const RatingItems = styled.div<{ mobileNone?: boolean }>`
    display: flex;
    align-items: center;
    margin-right: 1.2rem;

    @media only screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        display: ${({ mobileNone }) => (mobileNone ? 'none' : 'flex')};
        margin-right: 0.5rem;
    }
`;

const HasReplyWrapper = styled.div<{ hasReply: boolean }>`
    display: flex;
    align-items: center;

    @media only screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        display: ${({ hasReply }) => (hasReply ? 'none' : 'flex')};
        width: 50%;
        justify-content: center;
        border-left: 1px solid rgba(0, 0, 0, 0.17);
        order: 2;
    }
`;

const ReviewBody = styled(Body)`
    font-weight: 700;
    ${({ theme }) => getFontCss(theme.fontSizes.body.small)};
    color: ${({ theme }) => theme.colors.primary[80]};
`;

const StyledChat = styled(Chat)`
    margin-right: 0.5rem;
`;

const StyledReviewStar = styled(ReviewStar)`
    display: none;
    @media only screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        display: block;
        margin-right: 0.4rem;
    }
`;

const StyledDivider = styled.hr<{ hasReview: boolean }>`
    display: none;
    @media only screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        display: ${({ hasReview }) => (hasReview ? 'none' : 'block')};
        margin-top: 1.8rem;
        margin-bottom: 1.8rem;
        border: 1px solid rgba(0, 0, 0, 0.17);
    }
`;
